/**
 * Error Serialization Utility
 *
 * Safely extracts human-readable error messages from various error shapes.
 * Prevents "[object Object]" from appearing in error displays and Sentry logs.
 *
 * Handles:
 * - String errors
 * - Error instances
 * - Axios error responses (nested data structures)
 * - nShift structured errors (arrays with code/message/level)
 * - Generic objects with message/error properties
 *
 * Note: For nShift-specific booking errors, use extractUserFriendlyMessage
 * from nshiftErrors.ts which provides additional cleaning of technical prefixes.
 */

/**
 * Check if a value is an object (not null, not array)
 */
function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Check if an error is an Axios-style error with response data
 */
interface AxiosLikeError {
  response?: {
    data?: {
      message?: unknown;
      error?: unknown;
      errors?: unknown[];
      structuredErrors?: unknown[];
    };
  };
  message?: unknown;
}

function isAxiosLikeError(error: unknown): error is AxiosLikeError {
  return isObject(error) && 'response' in error;
}

/**
 * Extract a string message from a potentially nested value.
 * Recursively unwraps objects looking for a string message.
 */
function extractMessageFromValue(value: unknown, depth = 0): string | null {
  // Prevent infinite recursion
  if (depth > 5) {
    return null;
  }

  // Already a string
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  // Null/undefined
  if (value === null || value === undefined) {
    return null;
  }

  // Array - try to extract messages from each element
  if (Array.isArray(value)) {
    const messages: string[] = [];
    for (const item of value) {
      const msg = extractMessageFromValue(item, depth + 1);
      if (msg && !messages.includes(msg)) {
        messages.push(msg);
      }
    }
    return messages.length > 0 ? messages.join('\n') : null;
  }

  // Object - look for common message properties
  if (isObject(value)) {
    // Priority order for message properties
    const messageKeys = ['message', 'msg', 'error', 'errorMessage', 'text', 'description', 'original'];

    for (const key of messageKeys) {
      if (key in value) {
        const msg = extractMessageFromValue(value[key], depth + 1);
        if (msg) {
          return msg;
        }
      }
    }

    // Check for structured errors array
    if ('structuredErrors' in value && Array.isArray(value.structuredErrors)) {
      const msg = extractMessageFromValue(value.structuredErrors, depth + 1);
      if (msg) {
        return msg;
      }
    }

    // Check for errors array
    if ('errors' in value && Array.isArray(value.errors)) {
      const msg = extractMessageFromValue(value.errors, depth + 1);
      if (msg) {
        return msg;
      }
    }
  }

  // Number or boolean - convert to string
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return null;
}

/**
 * Serialize any error to a user-friendly string message.
 * Never returns "[object Object]" - always provides a meaningful message or fallback.
 *
 * @param error - Any error value (Error, string, object, Axios error, etc.)
 * @param fallbackMessage - Message to use if no meaningful error can be extracted
 * @returns A human-readable error message string
 */
export function serializeError(
  error: unknown,
  fallbackMessage = 'Ett oväntat fel uppstod',
): string {
  // Early return for null/undefined
  if (error === null || error === undefined) {
    return fallbackMessage;
  }

  // Already a string
  if (typeof error === 'string') {
    return error.trim() || fallbackMessage;
  }

  // Axios-style error with response (must check BEFORE instanceof Error,
  // because AxiosError extends Error and we want the response data message,
  // not the generic "Request failed with status code XXX")
  if (isAxiosLikeError(error)) {
    const responseData = error.response?.data;

    if (responseData) {
      // Try to extract from response data
      const msg = extractMessageFromValue(responseData);
      if (msg) {
        return msg;
      }
    }

    // Fallback to error.message
    const rootMsg = extractMessageFromValue(error.message);
    if (rootMsg) {
      return rootMsg;
    }
  }

  // Error instance
  if (error instanceof Error) {
    return error.message || fallbackMessage;
  }

  // Generic object - try to extract message
  if (isObject(error)) {
    const msg = extractMessageFromValue(error);
    if (msg) {
      return msg;
    }
  }

  // Array - try to extract messages
  if (Array.isArray(error)) {
    const msg = extractMessageFromValue(error);
    if (msg) {
      return msg;
    }
  }

  // Last resort - try JSON stringification for debugging (only in dev mode, not in tests)
  // Note: console.info is captured by Sentry in production but downgraded from error level
  if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
    try {
      const serialized = JSON.stringify(error);
      // Log for debugging but don't show to user
      if (serialized !== '{}' && serialized !== '[]') {
        // eslint-disable-next-line no-console
        console.info('[errorSerializer] Unknown error shape:', serialized);
      }
    } catch {
      // Ignore serialization errors
    }
  }

  return fallbackMessage;
}

/**
 * Serialize error specifically for API error responses.
 * Handles Axios error structure and backend API response formats.
 *
 * @param error - API error (typically Axios error or response object)
 * @param fallbackMessage - Message to use if no meaningful error can be extracted
 * @returns A human-readable error message string
 */
export function serializeApiError(
  error: unknown,
  fallbackMessage = 'Ett oväntat fel uppstod',
): string {
  return serializeError(error, fallbackMessage);
}

/**
 * Check if an error message indicates it's already been serialized properly
 * (i.e., it's not [object Object])
 *
 * @internal Utility for error boundary components - may be used in future error handling
 */
export function isValidErrorMessage(message: string): boolean {
  return !message.includes('[object Object]') &&
         !message.includes('[object Array]') &&
         message.trim().length > 0;
}
